<template lang="pug">
.sidecart.js-sidecart(data-sidecart @carttoggle="cartDisplay")
  .sidecart__overlay(v-if="sidecartOpen", @click="cartDisplay")
  transition(name="slide")
    .sidecart__drawer(v-if="sidecartOpen", data-sidecart-visible)
      .sidecart__header
        .sidecart__header-content
          h2.sidecart__title {{heading}}
            small &nbsp;{{ cartCountText }}
          button.button--icon.sidecart__close(@click="$store.dispatch('cart/closeSidecart')")
            .u-sr-only Close side cart
            Icon(name="close" height="30" width="30")
          .sidecart__progressbar-wrapper(v-if="this.sampleProducts.showSampleProducts && sampleProducts.showThresholdBar")
            p.sidecart__progressbar-heading(v-html="progressBarText")
            .sidecart__progressbar
              span(:style="'width:'+progressBarWidth+'%;'")
      .sidecart__container
        .sidecart__contents-wrapper(v-if="cartData.item_count > 0")
          .sidecart__contents
            CartLineItem(
              v-for="(item, index) in cartData.items"
              :item="item"
              :line="index + 1"
              :key="reload + index"
              :reload="upsellProductAdded"
              @updateCart="updateCart"
              :freeSampleProduct="isFreeSampleProduct(item.variant_id)")
            ul.sidecart__sample-product-list(v-if="this.sampleProducts.showSampleProducts && this.sampleProducts.products.length > 0")
              h6.sidecart__sample-product-heading(v-if="this.sampleProducts.heading" v-text="sampleProductsHeading")
              li.sidecart__sample-products-item(
                v-for="(product, index) in this.sampleProducts.products" :key="index"
                :class="{ disabled: !hasThresholdMet }")
                img.sidecart__sample-product-image(:src="product.images[0].sizes.xs | image('200x')" :alt="product.title")
                p.sidecart__sample-product-title(v-text="product.title")
                a.sidecart__sample-product-cta(
                  v-if="hasThresholdMet"
                  @click.prevent="addRemoveSampleProduct(product.variants[0].id)"
                  :class="{ disabled: !addToCartAllowed && !isSampleProductInCart(product.variants[0].id), active: isSampleProductInCart(product.variants[0].id) }"
                  v-text="isSampleProductInCart(product.variants[0].id) ? 'added!' : 'add'")
                span.sidecart__sample-product-cta(v-else class="disabled") add
            .upsell-wrapper
              h6.upsell-wrapper__header-text(v-text="upsellText")
          .sidecart__footer
            h5.sidecart__subtotal
              span Subtotal:
              span.subtotal__amount {{getCartTotal | moneyWithoutDecimals}}
            .sidecart__ctas
              a.button.button--secondary(href="/cart") view cart
              button.button.button--primary(@click="checkoutRedirect") Checkout
        .sidecart__empty(v-else key="empty")
          p.sidecart__empty-text {{ emptyText }}
</template>

<script>
  import { mapState } from 'vuex';
  import Icon from 'scripts/components/basic/Icon.vue';
  import CartLineItem from 'scripts/components/cart/CartLineItem.vue';

  export default {
    name: 'Sidecart',
    components: { Icon, CartLineItem },
    data() {
      return {
        giftEnabled: false,
        upsellProductAdded: false,
        reload: 0,
      }
    },
    props: {
      heading: String,
      emptyText: String,
      thresholdMessage: String,
      showUpsell: Boolean,
      upsellText: String,
      sampleProducts: {
        showSampleProducts: Boolean,
        showThresholdBar: Boolean,
        headingDisabled: String,
        heading: String,
        threshold: Number,
        thresholdBarText: String,
        maxProductsAllowed: Number,
        products: Object,
      },
    },
    computed: {
      ...mapState({
        sidecartOpen: (state) => state.cart.sidecartOpen,
        cartData: (state) => state.cart.checkout,
        items: (state) => state.cart.checkout.items,
        cartTotal: (state) => state.cart.checkout.total_price,
      }),
      getCartTotal() {
        return this.cartTotal;
      },
      cartTotalDecimal() {
        return this.cartTotal.toFixed(2) / 100;
      },
      cartCountText() {
        return this.cartData.item_count < 1 ? '' : `(${this.cartData.item_count})`;
      },
      progressBarWidth() {
        return this.cartTotalDecimal >= this.sampleProducts.threshold
        ? 100
        : (this.cartTotalDecimal * 100) / this.sampleProducts.threshold
      },
      progressBarText() {
        const diff = (this.sampleProducts.threshold - this.cartTotalDecimal).toFixed(2);
        const msg = this.thresholdMessage.replace('[$X]', `<strong>$${diff}</strong>`);
        return this.cartTotalDecimal >= this.sampleProducts.threshold ? this.sampleProducts.thresholdBarText : msg;
      },
      hasThresholdMet() {
        return this.cartTotalDecimal > 0 && this.cartTotalDecimal >= this.sampleProducts.threshold  ? true : false
      },
      cartTotalSampleProducts() {
        return this.items.filter((item) => item.properties && item.properties.productType === 'free-sample').length
      },
      addToCartAllowed() {
        return this.cartTotalSampleProducts < this.sampleProducts.maxProductsAllowed || this.cartTotalDecimal < this.sampleProducts.threshold
      },
      sampleProductsHeading() {
        return this.hasThresholdMet ? this.sampleProducts.heading : this.sampleProducts.headingDisabled
      },
    },
    watch: {
      cartTotal() {
        if(!this.hasThresholdMet) {
          this.cartRemoveAllFreeSamples();
        }
      }
    },
    methods: {
      checkoutRedirect() {
        if ( window.HAS_RECHARGE_SUBSCRIPTIONS ) {
            return window.forceReChargeProcessCart()
        }

        window.location.href = "/checkout";
      },
      cartDisplay() {
        this.$store.dispatch('cart/init');
        if (this.sidecartOpen) {
          this.$store.dispatch('cart/closeSidecart');
        } else {
          this.$store.dispatch('cart/openSidecart');
        }
      },
      isFreeSampleProduct(variant_id) {
        return this.items.some((item) => item.properties && item.properties.productType === 'free-sample' && item.variant_id === variant_id)
      },
      isSampleProductInCart(variant_id) {
        return this.items.some((item) => item.variant_id === variant_id)
      },
      cartRemoveAllFreeSamples() {
        let itemsToRemove = [];
        let keyToRemove = "";
        let itemToRemoveArray = this.items.filter((freeProduct) => freeProduct.properties.productType === 'free-sample');

        if (itemToRemoveArray.length > 0) {
          itemToRemoveArray.forEach((getRemoveItem) => {
              keyToRemove = getRemoveItem.key
              itemsToRemove.push({
              key: keyToRemove,
              quantity: 0
            });
          });
        }

        this.$store.dispatch('cart/removeItems', itemsToRemove)
      },
      addRemoveSampleProduct(variant_id) {
        // sample product in cart - REMOVE
        if (this.isSampleProductInCart(variant_id)) {
          const line = this.items.findIndex((item) => {
            return item.variant_id === variant_id
          }) + 1;
          this.$store.dispatch('cart/removeItem', line)

        // sample product not in cart - ADD
        } else {
          this.$store.dispatch('cart/addItem', {
            quantity: 1,
            id: variant_id,
            properties: {
              productType: 'free-sample',
              thresholdPrice: this.sampleProducts.threshold
            },
          });
        }
      },
      updateCart() {
        this.$forceUpdate();
      },
    },
    mounted() {
      window.addEventListener('reInitSideCart', () => {
        this.reload = this.reload + Math.floor(Math.random() * 10000)
      });
    }
  }
</script>

<style scoped lang="scss">
  .sidecart {}
  .sidecart__overlay {
    position: fixed;
    top: 0;
    right: 0;
    z-index: 9999;
    width: 100%;
    height: 100%;
    background-color: $color-transparent-black;
    cursor: close;
  }

  .sidecart__drawer {
    display: flex;
    position: fixed;
    top: 0;
    right: 0;
    z-index: 10000;
    width: 100%;
    height: 100%;
    flex-direction: column;
    background-color: var(--color-ivory);
    margin-right: -520px;

    @include tablet-up {
      border-left: 1px solid var(--color-grey-400);
      max-width: 520px;
    }

    &[data-sidecart-visible] {
      margin-right: 0;
    }
  }

  .sidecart__header {
    position: relative;
    flex-shrink: 0;
    border-bottom: 1px solid var(--color-grey-400);
  }

  .button--icon.sidecart__close {
    cursor: pointer;
  }

  .sidecart__header-background {
    padding-bottom: 27.5%;
    height: 0;
    background-size: 105%;
    background-repeat: no-repeat;
    background-position: top left;
  }

  .sidecart__close {
    background: transparent;
    border: 0;
    padding: 0;
    min-width: auto;
  }

  .sidecart__header-content {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    padding: 16px;
    width: 100%;
    flex-wrap: wrap;
  }

  .sidecart__title {
    margin: 0;
    font-size: rem(23);
    line-height: rem(29);
    text-align: center;
    display: flex;
    align-items: center;
    font-weight: var(--font-weight-normal);

    @include tablet-up {
      font-size: rem(20);
    }

    &:focus {
      box-shadow: none;
    }
  }

  .sidecart__count {
    font-size: rem(14);
    line-height: rem(20);

    @include tablet-up {
      font-size: rem(16);
    }
  }

  .sidecart__container {
    display: flex;
    flex-grow: 1;
    min-height: 0;
  }

  .sidecart__contents-wrapper {
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    flex-grow: 1;
  }

  .sidecart__contents {
    flex-grow: 1;
    padding: 0 rem(20) rem(20);
    overflow-y: auto;
    overflow-x: hidden;
    position: relative;
    &::-webkit-scrollbar {
      width: 10px;
    }
    &::-webkit-scrollbar-thumb {
      background-color: var(--color-charcoal);
    }
    &::-webkit-scrollbar-track {
      background-color: var(--color-off-white);
    }
  }

  // Progress bar
  .sidecart__progressbar-wrapper {
    padding: rem(10) 0 0 0;
    display: flex;
    flex-direction: column;
    z-index: 100;
    background-color: var(--color-ivory);
    width: 100%;
  }
  .sidecart__progressbar-heading {
    width: 100%;
    text-align: center;
    &,
    * {
      font-size: rem(16);
      color: var(--color-blue);
      font-family: var(--font-family-header-secondary);
      margin: 0 0 rem(6) 0;
    }
  }
  .sidecart__progressbar {
    border: 1px solid var(--color-blue);
    height: rem(12);
    padding: 2px;
    display: flex;

    span {
      display: inline-block;
      height: 100%;
      background-color: var(--color-blue);
      transition: all .3s ease-in-out;
    }
  }

  // Sample Products
  .sidecart__sample-product-list {
    padding: rem(25) 0;
    margin: 0;
    display: flex;
    flex-wrap: wrap;
    gap: rem(15);
    border-top: 1px solid var(--color-grey-400);
    border-bottom: 1px solid var(--color-grey-400);
  }

  .upsell-wrapper__header-text,
  .sidecart__sample-product-heading {
    width: 100%;
    font-weight: var(--font-weight-bold);
    font-size: rem(17);
    text-transform: uppercase;
    color: var(--color-charcoal);
    margin: 0;
    letter-spacing: 2px;
    font-family: var(--font-family-header-secondary);
  }
  .sidecart__sample-products-item {
    padding: 0;
    margin: 0;
    list-style: none;
    display: flex;
    flex-direction: column;
    flex: 1;
    max-width: 25%;

    &.disabled {
      opacity: 0.6;
    }
  }
  .sidecart__sample-product-image {
    display: block;
    width: 100%;
    border: 1px solid var(--color-grey-400);
    background-color: var(--color-white);
  }
  .sidecart__sample-product-title {
    text-align: center;
    font-size: rem(12);
    margin: rem(8) 0 rem(12) 0;
    color: var(--color-charcoal);
    font-family: var(--font-family-primary);
  }
  .sidecart__sample-product-cta {
    display: flex;
    margin-top: auto;
    border: 2px solid var(--color-blue);
    font-size: rem(12);
    font-weight: var(--font-weight-bolder);
    width: 100%;
    color: var(--color-blue);
    text-decoration: none;
    text-transform: uppercase;
    padding: 0;
    height: rem(30);
    align-items: center;
    justify-content: center;

    &.active,
    &:active,
    &:hover {
      background-color: var(--color-blue);
      color: var(--color-white);
    }

    &.disabled {
      color: var(--color-charcoal);
      border-color: var(--color-grey-500);
      background-color: var(--color-grey-500);
      pointer-events: none;
    }
  }

  // Upsell
  .upsell-wrapper {}
  .upsell-wrapper__header-text {
    padding: rem(25) 0 rem(30) 0;
  }

  // Checkout CTA
  .sidecart__ctas {
    display: flex;
    gap: rem(15);
  }
  .sidecart__ctas .button {
    flex: 1;
    text-transform: uppercase;
    font-size: rem(17);
    min-height: rem(42);
    max-height: rem(42);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0;
  }

  // Empty cart
  .sidecart__empty {
    display: flex;
    padding: 50px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    background-size: 181px;
    background-repeat: no-repeat;
    background-position: bottom right;

    @include tablet-up {
      background-size: 251px;
    }
  }
  .sidecart__empty-text {
    margin-bottom: 0;
    color: var(--color-charcoal);
    font-size: rem(23);
    line-height: rem(29);
    text-align: center;

    @include tablet-up {
      font-size: rem(24);
      line-height: rem(28);
    }
  }

  // Footer
  .sidecart__footer {
    display: flex;
    flex-direction: column;
    flex-shrink: 0;
    padding: 15px rem(20);
    border-top: 1px solid var(--color-grey-400);
  }
  .sidecart__subtotal {
    color: var(--color-charcoal);
    width: 100%;
    margin-bottom: 15px;
    .subtotal__amount {
      float: right;
    }
    * {
      font-size: rem(17);
      font-family: var(--font-family-header-secondary);
    }
  }
  .u-sr-only {
    border: 0;
    clip: rect(0 0 0 0);
    height: auto;
    margin: 0;
    overflow: hidden;
    padding: 0;
    position: absolute;
    width: 1px;
    white-space: nowrap;
  }

  // Transitions
  .slide-leave-active,
  .slide-enter-active {
    transition: .5s;
    @include tablet-up {
      transition: 1s;
    }
  }
  .slide-enter {
    transform: translate(100%, 0);
  }
  .slide-leave-to {
    transform: translate(100%, 0);
  }
</style>
